import { Suspense, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IInvoice, Item } from "@/types/invoice.type";
import {
  confirmInvoiceItems,
  getInvoiceById,
} from "@/common/services/invoice.api";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import { numberToCurrency } from "@/common/helpers/currency.helpers";
import { FaArrowLeft } from "react-icons/fa";
import { Modal } from "flowbite-react";
import { FormAddPurchaseOrder } from "../forms/FormAddPurchaseOrder.form";
import { Button } from "@/components/ui/button";
import { ConfirmationModal } from "@/common/components/ui/modal/ConfirmationModal";
const itemsHeaders = {
  numero_item: "Nº Item",
  codigo_produto: "Cód. Produto",
  descricao: "Descrição",
  quantidade_comercial: "Qtd.",
  valor_unitario_comercial: "Vl. Unitário",
  valor_bruto: "Vl. Total",
  purchaseOrderLink: "Ordem de Compra",
};

type RenderFunctions = {
  [key: string]: (
    value: unknown,
    item: unknown
  ) => JSX.Element | string | number;
};

export function IncomingInvoicesDetailsTable() {
  const { NFSEntryId: id } = useParams<{ NFSEntryId: string }>();
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useToastContext();

  const [invoice, setInvoice] = useState<IInvoice | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const allItemsLinked = useMemo(() => {
    if (!invoice?.itens) {
      return false;
    }
    return invoice.itens.every(
      (item) =>
        item.purchaseOrder &&
        item.purchaseOrder.purchaseOrderCode &&
        item.purchaseOrder.purchaseOrderItemId
    );
  }, [invoice]);

  useEffect(() => {
    if (!id) {
      notifyError("ID da nota fiscal inválido.");
      setIsLoading(false);
      return;
    }
    const loadInvoiceDetails = async () => {
      setIsLoading(true);
      const result = await getInvoiceById(id);
      if (!result.ok || !result.data) {
        setInvoice(null);
      } else {
        setInvoice(result.data);
      }
      setIsLoading(false);
    };
    loadInvoiceDetails();
  }, [id, notifyError]);

  const handleFormSaved = (updatedInvoice: IInvoice) => {
    setInvoice(updatedInvoice);
    setIsModalOpen(false);
  };

  const handleOpenModal = (item: Item) => {
    if (item.purchaseOrder) return;
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleExecuteConfirmation = async () => {
    if (!invoice?._id) return;

    if (!allItemsLinked) {
      notifyError(
        "Todos os itens devem estar vinculados a uma Ordem de Compra antes de confirmar."
      );
    }
    setIsConfirming(true);
    const result = await confirmInvoiceItems(invoice._id);
    if (result.ok) {
      notifySuccess(result.message || "Itens confirmados com sucesso!");
      const updatedInvoiceResult = await getInvoiceById(invoice._id);
      if (updatedInvoiceResult.ok) {
        setInvoice(updatedInvoiceResult.data);
      }
    } else {
      notifyError(result.message || "Ocorreu um erro ao confirmar os itens.");
    }
    setIsConfirming(false);
    setIsConfirmModalOpen(false);
  };

  const itemActions = [
    {
      label: "Adicionar OC",
      action: (item: unknown) => handleOpenModal(item as Item),
      disabled: (item: unknown) => !!(item as Item).purchaseOrder,
    },
  ];

  const renderers: RenderFunctions = {
    valor_unitario_comercial: (value) =>
      numberToCurrency(parseFloat((value as string) || "0")),
    valor_bruto: (value) =>
      numberToCurrency(parseFloat((value as string) || "0")),
    purchaseOrderLink: (_value, item) => {
      const fullItem = item as Item;
      return fullItem.purchaseOrder?.purchaseOrderCode || "N/A";
    },
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!invoice) {
    return (
      <div className="p-4 text-center">
        <h2>Nota Fiscal não encontrada.</h2>
        <button onClick={() => navigate(-1)} className="mt-4 btn btn-primary">
          Voltar
        </button>
      </div>
    );
  }

  const invoiceItems = invoice.itens ?? [];

  return (
    <Suspense fallback={<Loading />}>
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 btn btn-circle btn-sm"
            >
              <FaArrowLeft />
            </button>
            <h1 className="text-2xl font-bold">Detalhes da Nota Fiscal</h1>
          </div>
        </div>

        <div className="p-4 mb-6 bg-white rounded-lg shadow-md dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold border-b pb-2">
            Informações Gerais
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Emitente
              </label>
              <p className="text-base font-semibold">{invoice.nome_emitente}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Data de Emissão
              </label>
              <p className="text-base">
                {new Date(invoice.data_emissao).toLocaleDateString("pt-BR")}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Situação
              </label>
              <p className="text-base capitalize">
                {invoice.situacao.toLowerCase()}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Valor Total
              </label>
              <p className="text-base font-bold text-success">
                {numberToCurrency(parseFloat(invoice.valor_total || "0"))}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-lg font-semibold">Itens da Nota</h2>
            {!invoice.stock_launched && (
              <Button
                onClick={() => setIsConfirmModalOpen(true)}
                disabled={isConfirming}
              >
                {isConfirming
                  ? "Confirmando..."
                  : "Confirmar Itens e Lançar Estoque"}
              </Button>
            )}
          </div>
          <GenericTable
            headers={itemsHeaders}
            data={invoiceItems as unknown as Record<string, unknown>[]}
            renders={renderers}
            actions={itemActions}
            entity="employees"
          />
        </div>
      </div>

      {isModalOpen && selectedItem && invoice?._id && (
        <Modal show={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <Modal.Header>
            <h2 className="text-black">{`Vincular OC - Item ${selectedItem.numero_item}`}</h2>
          </Modal.Header>
          <Modal.Body>
            <FormAddPurchaseOrder
              invoiceId={invoice._id}
              item={selectedItem}
              onSave={handleFormSaved}
              onClose={() => setIsModalOpen(false)}
            />
          </Modal.Body>
        </Modal>
      )}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        title="Confirmar Lançamento de Estoque"
        message="Tem certeza que deseja confirmar os itens e lançar o estoque? Esta ação não pode ser desfeita."
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleExecuteConfirmation}
        isConfirming={isConfirming}
      />
    </Suspense>
  );
}
